import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Box, Typography} from "@mui/material";
import IndustryManagementViewWithoutAccessColumn from "./without-access";
import Utils from "../../../../../../../core/services/utils/utils";
import IndustryManagementViewWitAccessColumn from "./with-access";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useIndustry from "../../../../../../hooks/use-industry";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";


const IndustryManagementView = () => {
    const [withAccessIndustries, setWithAccessIndustries] = useState([]);
    const [withoutAccessIndustries, setWithoutAccessIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [industryCloned, setIndustryCloned] = useState(false);
    const [movingIndustry, setMovingIndustry] = useState(false);
    const {invalidateIndustriesCache} = useIndustry();
    const isMounted = useIsMounted();

    const {configureNotebookTags} = useConfigureNotebook('inventory-management-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsIndustryManagement);
        configureBehavioralTool((tags) => tags.settingsIndustryManagement);
    }, [configureBehavioralTool, configureNotebookTags]);


    const industries = useMemo(() => [...withAccessIndustries, ...withoutAccessIndustries], [withAccessIndustries, withoutAccessIndustries]);

    /**
     * Fetch industries from server
     * @type {(function(): Promise<void>)|*}
     */
    const getIndustries = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllIndustries();
        const partnerResponse = await Api.getPartnerIndustries();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag && partnerResponse.resultFlag) {
            const partnerIndustryIds = partnerResponse?.data?.map((industry) => industry.id) ?? [];
            response.data = response.data.map((industry) => {
                const partnerIndustry = partnerResponse.data.find((partnerIndustry) => partnerIndustry.id === industry.id);
                return {
                    ...industry,
                    hasAccess: partnerIndustryIds.includes(industry.id),
                    isDefault: partnerIndustry?.isDefault ?? false,
                }
            })
            setWithAccessIndustries(response.data.filter(e => e.hasAccess).map((e, i) => ({...e, orderIndex: i})));
            setWithoutAccessIndustries(response.data.filter(e => !e.hasAccess).map((e, i) => ({...e, orderIndex: i})));
        }
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - fetch industries from server
     */
    useEffect(() => {
        void getIndustries();
    }, [getIndustries])

    /**
     * Add industry to access list
     * @type {(function(*, *, boolean=): Promise<void>)}
     */
    const addToAccessedIndustries = useCallback(async (industry, evt, fromButton, oldIndex) => {
        const {from, to, oldDraggableIndex, newDraggableIndex} = evt;
        if (from === to && !fromButton) {
            setWithoutAccessIndustries(prevState => Utils.reorderList(prevState, oldDraggableIndex, newDraggableIndex, 'orderIndex'))
            return;
        }

        setMovingIndustry(true);

        setWithoutAccessIndustries(prevState => prevState.filter(e => e.id !== industry.id).map((e, i) => ({...e, orderIndex: i})))
        setWithAccessIndustries(prevState => Utils.reorderList([...prevState, {
            ...industry,
            orderIndex: prevState.length
        }], prevState.length, fromButton ? prevState.length : Math.min(newDraggableIndex, prevState.length), 'orderIndex'))
        const forApi = [...withAccessIndustries.map(e => e.id), industry.id]
        const response = await Api.setIndustryAccess(forApi)
        if (!isMounted())
            return;
        setMovingIndustry(false);
        if (!response?.resultFlag) {
            setWithAccessIndustries(prevState => prevState.filter(e => e.id !== industry.id).map((e, i) => ({...e, orderIndex: i})))
            setWithoutAccessIndustries(prevState => Utils.reorderList([...prevState, {
                ...industry,
                orderIndex: prevState.length
            }], prevState.length, fromButton ? oldIndex : Math.min(oldDraggableIndex, prevState.length), 'orderIndex'))
        } else {
            void invalidateIndustriesCache();
        }
    }, [invalidateIndustriesCache, isMounted, withAccessIndustries])

    /**
     * Remove industry from access list
     * @type {(function(*, *): Promise<void>)|*}
     */
    const removeAccessedIndustry = useCallback(async (industry, oldIndex) => {
        setWithAccessIndustries(prevState => prevState.filter(e => e.id !== industry.id).map((e, i) => ({...e, orderIndex: i})))
        setWithoutAccessIndustries(prevState => Utils.reorderList([...prevState, {...industry, orderIndex: prevState.length}]));
        const forApi = [...withAccessIndustries.filter(e => e.id !== industry.id).map(e => e.id)]
        const response = await Api.setIndustryAccess(forApi)
        if (!isMounted())
            return;
        if (!response?.resultFlag) {
            setWithoutAccessIndustries(prevState => prevState.filter(e => e.id !== industry.id).map((e, i) => ({...e, orderIndex: i})))
            setWithAccessIndustries(prevState => Utils.reorderList([...prevState, {
                ...industry,
                orderIndex: prevState.length
            }], prevState.length, oldIndex, 'orderIndex'))
        } else {
            void invalidateIndustriesCache();
        }
        return response?.resultFlag;
    }, [invalidateIndustriesCache, isMounted, withAccessIndustries])

    return (
        <>
            <Box sx={{display: 'flex', gap: 1, paddingBlockEnd: 1, position: 'relative', flexWrap: 'wrap'}}>
                {
                    (loading || industries.length > 0) &&
                    <>
                        <IndustryManagementViewWitAccessColumn
                            id={'with-access'}
                            title={"List of Industries that you have Access"}
                            data={withAccessIndustries}
                            moveIndustry={removeAccessedIndustry}
                            movingIndustry={movingIndustry}
                            industryCloned={industryCloned}
                            loading={loading}
                        />
                        <IndustryManagementViewWithoutAccessColumn
                            id={'without-access'}
                            title={"Available Industries"}
                            data={withoutAccessIndustries}
                            setIndustryCloned={setIndustryCloned}
                            moveIndustry={addToAccessedIndustries}
                            movingIndustry={movingIndustry}
                            loading={loading}
                        />
                    </>
                }
                {
                    !loading && industries.length === 0 &&
                    <Box marginBlock={10} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                        <Typography variant={'body2'} textAlign={'center'} marginBlockEnd={2}>
                            You have no industries to manage.
                        </Typography>
                    </Box>

                }
            </Box>
        </>
    );
}

export default IndustryManagementView;
